import { Card, Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { keyBy } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type InventoryModelOptionSpecsFragment } from '../../../api/fragments/InventoryModelOptionSpecs';
import { type InventoryOptionSpecsFragment } from '../../../api/fragments/InventoryOptionSpecs';
import { type InventorySubModelOptionSpecsFragment } from '../../../api/fragments/InventorySubModelOptionSpecs';
import { type InventoryVariantOptionSpecsFragment } from '../../../api/fragments/InventoryVariantOptionSpecs';
import { useGetInventoryOptionsQuery } from '../../../api/queries/getInventoryOptions';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import DisplayField from '../../../components/fields/DisplayField';
import SelectField, { type SelectOption } from '../../../components/fields/SelectField';
import { ALL_OPTION, type FormValues } from './utils';

type StateOptions<T> = {
    options: Omit<T, '__typename'>[];
    ids: string[];
};

type SelectionSectionProps = {
    optionsMode?: keyof Omit<InventoryOptionSpecsFragment, '__typename'>;
    setRequireDealer: (value: boolean) => void;
};

export const SelectionSectionFields = ({ optionsMode, setRequireDealer }: SelectionSectionProps) => {
    const { t } = useTranslation(['inventoryDetails']);

    const company = useCompany(true);

    const { dealerOptions, dealerIds, dealersFromApi } = useMultipleDealerIds();

    const mappedDealers = useMemo(() => keyBy('id', dealersFromApi), [dealersFromApi]);

    const { values, setFieldValue } = useFormikContext<FormValues>();

    const [models, setModelOptions] = useState<StateOptions<InventoryModelOptionSpecsFragment>>({
        options: [],
        ids: [],
    });

    const [subModels, setSubModelOptions] = useState<StateOptions<InventorySubModelOptionSpecsFragment>>({
        options: [],
        ids: [],
    });

    const [variants, setVariantOptions] = useState<StateOptions<InventoryVariantOptionSpecsFragment>>({
        options: [],
        ids: [],
    });

    const companyId = useMemo(() => {
        if (company) {
            return company.id;
        }

        if (values.dealerId) {
            // get company id for this dealer
            return mappedDealers[values.dealerId].company.id;
        }

        return undefined;
    }, [company, values.dealerId]);

    const { data, loading } = useGetInventoryOptionsQuery({
        fetchPolicy: 'cache-and-network', // the options maybe later change to network-first
        variables: {
            filters: {
                companyId,
                dealerIds: values.dealerId ? [values.dealerId] : dealerIds,
            },
        },
    });

    const moduleOptions = useMemo(() => data?.options?.[optionsMode]?.modules ?? [], [data, optionsMode]);

    const selectedModule = useMemo(
        () => moduleOptions?.find(module => module.value === values.moduleId),
        [moduleOptions, values.moduleId]
    );

    // Dealer
    const usedDealerOptions = useMemo(
        () => dealerOptions.filter(option => dealerIds.includes(option.value)),
        [dealerOptions, dealerIds]
    );

    // Handle dealer side effect
    useEffect(() => {
        if (usedDealerOptions.length === 1) {
            setFieldValue('dealerId', usedDealerOptions[0].value);
        } else {
            setFieldValue('dealerId', null);
        }
    }, [setFieldValue, usedDealerOptions]);

    useEffect(() => {
        if (models.options.length === 1) {
            setFieldValue('modelId', models.options[0].value);
        } else {
            setFieldValue('modelId', null);
        }
    }, [models.options, setFieldValue]);

    useEffect(() => {
        if (subModels.options.length === 1) {
            setFieldValue('subModelId', subModels.options[0].value);
        } else {
            setFieldValue('subModelId', null);
        }
    }, [setFieldValue, subModels.options]);

    useEffect(() => {
        if (variants.options.length === 1) {
            setFieldValue('variantId', variants.options[0].value);
        } else {
            setFieldValue('variantId', null);
        }
    }, [setFieldValue, variants.options]);

    // handle module side effect
    useEffect(() => {
        if (values.moduleId) {
            setFieldValue('modelId', null);
            setFieldValue('subModelId', null);
            setFieldValue('variantId', null);

            const modelOptions = selectedModule?.models ?? [];

            setModelOptions({
                options:
                    modelOptions.length > 1
                        ? [
                              {
                                  label: t('inventoryDetails:labels.allModels'),
                                  value: ALL_OPTION,
                                  subModels: [],
                                  variants: [],
                              },
                              ...modelOptions,
                          ]
                        : modelOptions,
                ids: modelOptions.map(model => model.value),
            });
        }
    }, [selectedModule, setFieldValue, t, values.moduleId]);

    useEffect(() => {
        if (values.moduleId && !moduleOptions.some(({ value }) => value === values.moduleId)) {
            // if module does not exist in options
            // reset module id
            setFieldValue('moduleId', null);
        }
    }, [moduleOptions, setFieldValue, values.moduleId]);

    // handle model side effect
    useEffect(() => {
        if (values.modelId) {
            const modelIds = values.modelId === ALL_OPTION ? models.ids : [values.modelId];
            setFieldValue('modelIds', modelIds);

            const filteredModelOptions = models.options.filter(option => modelIds.includes(option.value));

            const subModelOptions =
                filteredModelOptions.flatMap(option => option.subModels).filter(subModule => !!subModule?.value) ?? [];
            setSubModelOptions({
                options:
                    subModelOptions.length > 1
                        ? [
                              {
                                  label: t('inventoryDetails:labels.allSubModels'),
                                  value: ALL_OPTION,
                              },
                              ...subModelOptions,
                          ]
                        : subModelOptions,
                ids: subModelOptions?.map(subModel => subModel.value) ?? [],
            });

            if (subModelOptions.length === 1) {
                setFieldValue('subModelId', subModelOptions?.[0]?.value ?? null);
            } else {
                setFieldValue('subModelId', null);
            }

            const variantOptions = filteredModelOptions.flatMap(model => model.variants) ?? [];
            setVariantOptions({
                options:
                    variantOptions.length > 1
                        ? [
                              {
                                  label: t('inventoryDetails:labels.allVariants'),
                                  value: ALL_OPTION,
                              },
                              ...variantOptions,
                          ]
                        : variantOptions,
                ids: variantOptions?.map(variant => variant.value) ?? [],
            });

            if (variantOptions.length === 1) {
                setFieldValue('variantId', variantOptions?.[0]?.value ?? null);
            } else {
                setFieldValue('variantId', null);
            }
        }
    }, [models, setFieldValue, t, values.modelId]);

    // handle sub model side effect
    useEffect(() => {
        if (values.subModelId) {
            const subModelIds = values.subModelId === ALL_OPTION ? subModels.ids : [values.subModelId];
            setFieldValue('subModelIds', subModelIds);

            const subModelOptions = subModels.options.filter(option => subModelIds.includes(option.value));
            const variantOptions = subModelOptions.flatMap(subModel => subModel.variants) ?? [];

            setVariantOptions({
                options:
                    variantOptions.length > 1
                        ? [
                              {
                                  label: t('inventoryDetails:labels.allVariants'),
                                  value: ALL_OPTION,
                              },
                              ...variantOptions,
                          ]
                        : variantOptions,
                ids: variantOptions.map(variant => variant.value),
            });
        }
    }, [setFieldValue, subModels, t, values.subModelId]);

    // handle variant side effect
    useEffect(() => {
        if (values.variantId) {
            const variantIds = values.variantId === ALL_OPTION ? variants.ids : [values.variantId];
            setFieldValue('variantIds', variantIds);
        }
    }, [setFieldValue, values.variantId, variants.ids]);

    return (
        <Row gutter={14}>
            <Col lg={8} xs={24}>
                {moduleOptions.length === 0 ? (
                    <DisplayField
                        {...t('inventoryDetails:fields.module', { returnObjects: true })}
                        key="moduleId"
                        value="No Data"
                    />
                ) : (
                    <SelectField
                        {...t('inventoryDetails:fields.module', { returnObjects: true })}
                        loading={loading}
                        name="moduleId"
                        options={moduleOptions}
                        required
                        showSearch
                    />
                )}
            </Col>
            <Col lg={8} xs={24}>
                {usedDealerOptions.length === 0 ? (
                    <DisplayField
                        {...t('inventoryDetails:fields.dealer', { returnObjects: true })}
                        key="dealerId"
                        value="No Data"
                    />
                ) : (
                    <SelectField
                        {...t('inventoryDetails:fields.dealer', { returnObjects: true })}
                        disabled={usedDealerOptions.length < 2}
                        name="dealerId"
                        options={usedDealerOptions}
                        required
                        showSearch
                    />
                )}
            </Col>
            <Col lg={8} xs={24}>
                {models.options.length === 0 ? (
                    <DisplayField
                        {...t('inventoryDetails:fields.model', { returnObjects: true })}
                        key="modelId"
                        value="No Data"
                    />
                ) : (
                    <SelectField
                        {...t('inventoryDetails:fields.model', { returnObjects: true })}
                        name="modelId"
                        options={models.options}
                        required
                        showSearch
                    />
                )}
            </Col>
            <Col lg={8} xs={24}>
                {subModels.options.length === 0 ? (
                    <DisplayField
                        {...t('inventoryDetails:fields.submodel', { returnObjects: true })}
                        key="subModelId"
                        value="No Data"
                    />
                ) : (
                    <SelectField
                        {...t('inventoryDetails:fields.submodel', { returnObjects: true })}
                        disabled={subModels.options?.length === 0 || !values.modelId}
                        name="subModelId"
                        options={subModels.options as SelectOption<string>[]}
                        showSearch
                    />
                )}
            </Col>
            <Col lg={8} xs={24}>
                {variants.options.length === 0 ? (
                    <DisplayField
                        {...t('inventoryDetails:fields.variant', { returnObjects: true })}
                        key="variantId"
                        value="No Data"
                    />
                ) : (
                    <SelectField
                        {...t('inventoryDetails:fields.variant', { returnObjects: true })}
                        disabled={!values.modelId || variants.options?.length === 0}
                        name="variantId"
                        options={variants.options}
                        showSearch
                    />
                )}
            </Col>
        </Row>
    );
};

const SelectionSection = ({ optionsMode = 'create', setRequireDealer }: SelectionSectionProps) => {
    const { t } = useTranslation(['inventoryDetails']);

    return (
        <Card title={t('inventoryDetails:sections.selection')}>
            <SelectionSectionFields optionsMode={optionsMode} setRequireDealer={setRequireDealer} />
        </Card>
    );
};

export default SelectionSection;
